import { SearchParams } from '@components/CustomerSearch/components/CustomerSearchBox/CustomerSearchBox';
import { valtioDevTools } from '@lib/utils/devTools';
import { proxy, subscribe } from 'valtio';
import { IdName } from '@lib/utils/id-name';

export type DragAndDropItemDelta = {
  x: number;
  y: number;
};

type InboxState = {
  selectedTaskId: string | undefined;
  selectedTenantForOutboundCall: IdName | null;
  previousSelectedTaskId: string | undefined;
  missedCallsForQueue: Record<string, number>;
  missedCallsDid: number;
  showClassificationModalForTaskId: string;
  home: {
    searchParams?: SearchParams;
    selectedCustomer?: {
      tenantId: string;
      customerId: string;
    };
  };
  callBarPositionDeltas: Record<string, DragAndDropItemDelta>;
};

export const defaultState = {
  selectedTaskId: undefined,
  selectedTenantForOutboundCall: null,
  previousSelectedTaskId: undefined,
  missedCallsForQueue: {},
  missedCallsDid: 0,
  showClassificationModalForTaskId: '',
  home: {
    searchParams: {
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    },
    selectedCustomer: undefined,
  },
  callBarPositionDeltas: {},
};

export function getInitialState(): InboxState {
  const stored = localStorage.getItem('inbox');
  const state = stored ? JSON.parse(stored) : defaultState;

  return {
    ...state,
    home: state.home
      ? { ...defaultState.home, ...state.home }
      : defaultState.home,
    missedExternalCallsForQueue: state.missedExternalCallsForQueue,
    selectedTenantForOutboundCall:
      state.selectedTenantForOutboundCall ??
      defaultState.selectedTenantForOutboundCall,
    callBarPositionDeltas: state.callBarPositionDeltas
      ? state.callBarPositionDeltas
      : {},
  };
}

export const inboxState = proxy<InboxState>(getInitialState());

valtioDevTools(inboxState, 'inboxState');

export const resetInboxState = () => {
  inboxState.selectedTenantForOutboundCall = null;
  inboxState.selectedTaskId = undefined;
  inboxState.previousSelectedTaskId = undefined;
  inboxState.missedCallsForQueue = {};
  inboxState.missedCallsDid = 0;
  inboxState.showClassificationModalForTaskId = '';
  inboxState.home = {
    searchParams: {
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    },
    selectedCustomer: undefined,
  };
  inboxState.callBarPositionDeltas = {};
};

const setSelectedTaskId = (taskId: string | undefined) => {
  inboxState.previousSelectedTaskId = inboxState.selectedTaskId;
  inboxState.selectedTaskId = taskId;
};

const restorePrevSelectedTaskId = () => {
  // if previousSelectedTaskId is the same as selectedTaskId, then clear it and show the home inbox
  inboxState.selectedTaskId =
    inboxState.selectedTaskId === inboxState.previousSelectedTaskId
      ? undefined
      : inboxState.previousSelectedTaskId;
  inboxState.previousSelectedTaskId = undefined;
};

const setHomeInbox = () => {
  setSelectedTaskId(undefined);
};

const setInboxHomeSearchParams = (searchParams: SearchParams) => {
  if (!inboxState.home) {
    inboxState.home = {};
  }
  inboxState.home.searchParams = searchParams;
};

const setInboxHomeCustomer = (selectedCustomer: any) => {
  if (!inboxState.home) {
    inboxState.home = {};
  }
  inboxState.home.selectedCustomer = selectedCustomer;
};

const removeCallBarPositionDelta = (callBarId: string) => {
  delete inboxState.callBarPositionDeltas[callBarId];
};

const setCallBarPositionDeltas = (
  callBarPositionDeltas: Record<string, DragAndDropItemDelta>,
) => {
  inboxState.callBarPositionDeltas = callBarPositionDeltas;
};

const setTenantForOutboundCall = (tenant: IdName | null) => {
  inboxState.selectedTenantForOutboundCall = tenant;
};

export const inboxActions = {
  setSelectedTaskId,
  setHomeInbox,
  restorePrevSelectedTaskId,
  setInboxHomeSearchParams,
  setInboxHomeCustomer,
  removeCallBarPositionDelta,
  setCallBarPositionDeltas,
  setTenantForOutboundCall,
};

subscribe(inboxState, () => {
  localStorage.setItem('inbox', JSON.stringify(inboxState));
});
